import { useCallback } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { fetchDownloadDicomImageBySOP } from '~/backend/connector-api/connector-components';
import { StructureSet } from '~/domain/patient/structureset';
import { createAvailableStructureSetViewFromDomain } from '~/domain/projections/factories';
import { AvailableStructureSetView } from '~/domain/projections/views';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';
import useBusyIndicator from '~/hooks/useBusyIndicator';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import { structureSetStateFamily } from '~/state/atoms/structureSet/structureSetAtoms';
import { notifyStructureSetNotShown } from '~/utils/notifySelectionRefusal';

/**
 * Makes a structure set the active one: a set the patient holds at once,
 * from its cached state when there is one; a set known only from the
 * session once it is downloaded and imported. A downloaded set is first
 * offered to `askBeforeImageChange`, because only the download tells which
 * image it is on: when that asks, the set stays downloaded but not active
 * until the user confirms. A downloaded set whose image is not loaded
 * cannot be shown, and the user is told so. A failed download rejects and
 * activates nothing.
 */
export default function useStructureSetActivation(): {
    activate: (
        set: AvailableStructureSetView,
        askBeforeImageChange: (set: AvailableStructureSetView) => boolean
    ) => Promise<void>;
    // Activates a set the patient holds, asking nothing: for a set a workflow chose, not the user.
    activateHeld: (structureSet: StructureSet) => Promise<void>;
} {
    const { setEnvironmentStructureSet, initializeEnvironment } =
        useViewportEnvironment();
    const { processImportedFiles } = useProcessDicomFiles();
    const patient = useSelectedPatient();
    const sessionData = useRecoilValue(sessionDataAtom);
    const { showBusy, hideBusy } = useBusyIndicator();

    const activateHeld = useRecoilCallback(
        ({ snapshot }) =>
            async (structureSet: StructureSet) => {
                const cached = await snapshot.getPromise(
                    structureSetStateFamily(structureSet.dicomUid)
                );
                setEnvironmentStructureSet(
                    [cached ?? structureSet],
                    patient?.dataSet.PatientID ?? ''
                );
            },
        [setEnvironmentStructureSet, patient]
    );

    const downloadAndActivate = useCallback(
        async (
            sopInstanceUid: string,
            askBeforeImageChange: (set: AvailableStructureSetView) => boolean
        ) => {
            const payload = sessionData?.payload;
            const patientId = payload?.patient?.id;
            if (!payload || !patientId) {
                return;
            }
            await showBusy('Downloading structure set...');
            try {
                const structureSetBlob = await fetchDownloadDicomImageBySOP({
                    pathParams: {
                        patientID: patientId,
                        modality: 'RTSTRUCT',
                        sopInstanceUID: sopInstanceUid,
                    },
                });
                const files = [
                    new File(
                        [structureSetBlob],
                        `${payload.context_structure_set.sop_instance_uid}.dcm`
                    ),
                ];
                const processedItems = await processImportedFiles(files);
                if (!processedItems) {
                    return;
                }
                const downloaded = processedItems.structureSets.find(
                    (structureSet) => structureSet.dicomUid === sopInstanceUid
                );
                if (
                    downloaded?.image &&
                    askBeforeImageChange(
                        createAvailableStructureSetViewFromDomain(downloaded)
                    )
                ) {
                    return;
                }
                initializeEnvironment(processedItems);
                if (downloaded && !downloaded.image) {
                    notifyStructureSetNotShown();
                }
            } finally {
                hideBusy();
            }
        },
        [
            sessionData,
            showBusy,
            hideBusy,
            processImportedFiles,
            initializeEnvironment,
        ]
    );

    const activate = useCallback(
        async (
            set: AvailableStructureSetView,
            askBeforeImageChange: (set: AvailableStructureSetView) => boolean
        ) => {
            const held = set.structureSetId
                ? patient?.getStructureSetById(set.structureSetId)
                : patient?.structureSets.find(
                      (structureSet) =>
                          structureSet.dicomUid === set.sopInstanceUid
                  );
            if (held) {
                await activateHeld(held);
            } else if (!set.structureSetId) {
                await downloadAndActivate(
                    set.sopInstanceUid,
                    askBeforeImageChange
                );
            }
        },
        [patient, activateHeld, downloadAndActivate]
    );

    return { activate, activateHeld };
}
